import type { MutationBaseOptions } from '@apollo/client/core/watchQueryOptions'

import { useAccount } from '~publish/contexts/AccountContext'
import { GetAccount } from '~publish/contexts/AccountContext/AccountContext'
import type { Preferences } from '~publish/gql/graphql'
import { graphql, type ResultOf } from '~publish/graphql'
import { useTypedMutation } from '~publish/hooks/useTypedMutation'
import { client } from '~publish/legacy/apollo-client'

const withDefaults = (
  preferences: Partial<Preferences> | undefined | null,
): NonNullable<Preferences> => {
  return {
    __typename: 'Preferences',
    timeFormat: preferences?.timeFormat ?? '24',
    startOfWeek: preferences?.startOfWeek ?? 'monday',
    defaultScheduleOption: preferences?.defaultScheduleOption ?? 'Queue',
  }
}

export const UpdateAccountPreferences = graphql(`
  mutation UpdateAccountPreferences($input: UpdateAccountPreferencesInput!) {
    updateAccountPreferences(input: $input) {
      __typename
      ... on UpdateAccountPreferencesSuccess {
        preferences {
          timeFormat
          startOfWeek
          defaultScheduleOption
        }
      }
      ... on InvalidInputError {
        message
      }
    }
  }
`)

type MutationResult = ResultOf<
  typeof UpdateAccountPreferences
>['updateAccountPreferences']

type UpdatedPreferences = Extract<
  NonNullable<MutationResult>,
  { __typename: 'UpdateAccountPreferencesSuccess' }
>

const isSuccessFullResponse = (
  response: MutationResult | undefined,
): response is UpdatedPreferences => {
  return response?.__typename === 'UpdateAccountPreferencesSuccess'
}

/**
 * Custom hook to update and Accounts start of week preference mutation.
 * @returns {UpdateStartOfWeekPreferenceResponse} The function to trigger updating the start of week preference.
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/explicit-function-return-type
export const useUpdateAccountPreferences = () => {
  const { account } = useAccount()
  return useTypedMutation(
    UpdateAccountPreferences,
    (data) => data.updateAccountPreferences,
    {
      successTypename: 'UpdateAccountPreferencesSuccess',
      optimisticResponse: (
        variables,
        { IGNORE },
      ): MutationBaseOptions['optimisticResponse'] => {
        const currentData = client.cache.readQuery({
          query: GetAccount,
        })

        const { defaultScheduleOption, timeFormat, startOfWeek } =
          currentData?.account?.preferences ?? {}

        if (!defaultScheduleOption || !timeFormat || !startOfWeek) {
          return IGNORE
        }

        const updatedPreferences: Preferences = {
          defaultScheduleOption:
            variables.input.defaultScheduleOption ?? defaultScheduleOption,
          timeFormat: variables.input.timeFormat ?? timeFormat,
          startOfWeek: variables.input.startOfWeek ?? startOfWeek,
        }

        return {
          __typename: 'Mutation',
          updateAccountPreferences: {
            __typename: 'UpdateAccountPreferencesSuccess',
            preferences: updatedPreferences,
          },
        }
      },
      update: (cache, { data }) => {
        const result = data?.updateAccountPreferences
        if (isSuccessFullResponse(result) && account?.id != null) {
          const cacheId = cache.identify({
            __typename: 'Account',
            id: String(account.id),
          })

          if (cacheId) {
            cache.modify({
              id: cacheId,
              fields: {
                preferences() {
                  return {
                    __typename: 'Preferences',
                    timeFormat: result.preferences.timeFormat ?? '24',
                    startOfWeek: result.preferences.startOfWeek ?? 'monday',
                    defaultScheduleOption:
                      result.preferences.defaultScheduleOption ?? 'Queue',
                  }
                },
              },
            })
          }
        }
      },
    },
  )
}
